<template>
  <div class="metricas-cocina">
    <!-- Encabezado con el mismo ritmo que "Tu periodo" / "Tus ingresos". -->
    <div class="metricas-cocina__header">
      <h2 class="metricas-cocina__titulo">
        Tu cocina
        <span class="metricas-cocina__rango" v-if="rango">
          Del {{ fmtFecha(rango.desde) }} al {{ fmtFecha(rango.hasta) }}
        </span>
      </h2>
      <SelectorPeriodo :dias="30" @cambio="alCambiarPeriodo" />
    </div>

    <el-alert
      v-if="noDisponible"
      title="Métricas no disponibles en este momento"
      description="La fuente de datos no está respondiendo. Volvé a intentar en unos minutos; si sigue igual, avisá a Operaciones."
      type="info"
      :closable="false"
      show-icon
      class="mb-3"
    ></el-alert>

    <div v-else-if="cargando" class="metricas-cocina__cargando">Cargando métricas…</div>

    <template v-else-if="resumen">
      <div class="metricas-cocina__cards">
        <CardMetricaCocina
          title="% Reclamos"
          icono="ni ni-support-16"
          :valor="resumen.pct_reclamos"
          :estado="resumen.pct_reclamos_estado"
          formato="porcentaje"
          :contexto="resumen.reclamos_n + ' de ' + resumen.pedidos + ' pedidos'"
          pie="Menos es mejor"
          motivo="Aún no hay pedidos en el periodo"
          ayuda="Reclamos recibidos sobre pedidos facturables del periodo. Se excluyen los reclamos de prueba."
          enlace="/foodcourt/claims"
        />
        <CardMetricaCocina
          title="Rating apps"
          icono="ni ni-satisfied"
          :valor="resumen.rating"
          :estado="resumen.rating_estado"
          formato="estrellas"
          :contexto="resumen.rating_n + ' calificaciones'"
          pie="Promedio móvil de 30 días"
          motivo="Todavía no hay calificaciones"
          ayuda="Promedio móvil de 30 días de la calificación al restaurante (1 a 5). Se usa móvil y no diario porque con 0 a 2 votos por día el número sería ruido."
          enlace="/foodcourt/feedback"
        />
        <CardMetricaCocina
          title="Tiempo de preparación"
          icono="ni ni-watch-time"
          :valor="resumen.prep_avg_min"
          :estado="resumen.prep_estado"
          formato="minutos"
          contexto="Promedio del periodo"
          pie="Menos es mejor"
          motivo="Aún no hay pedidos con tiempo medido"
          ayuda="Minutos promedio entre que la cocina acepta el pedido y lo despacha. No cuenta los pedidos de pre-apertura."
          enlace="/foodcourt/tiempos-preparacion"
        />
        <CardMetricaCocina
          title="% Carta activa"
          icono="ni ni-collection"
          :valor="resumen.carta_activa_pct"
          :estado="resumen.carta_activa_estado"
          formato="porcentaje"
          :contexto="contextoCarta"
          pie="Más es mejor"
          motivo="Sin medición en el periodo"
          ayuda="Porcentaje del horario operativo con la carta disponible. Los domingos la fuente no reporta este dato."
          enlace="/foodcourt/carta-disponibilidad"
        />
        <!--
          Mide otra cosa que "% Carta activa" y por eso van las dos: esta es
          la COCINA cerrada cuando debia estar abierta; la otra son los PLATOS
          apagados con la cocina ya abierta. No suman 100 entre si.
        -->
        <CardMetricaCocina
          title="% Tiempo no disponible"
          icono="ni ni-button-power"
          :valor="resumen.time_no_disp_pct"
          :estado="resumen.time_no_disp_estado"
          formato="porcentaje"
          contexto="Promedio del periodo"
          pie="Menos es mejor"
          motivo="Sin medición en el periodo"
          ayuda="Porcentaje del horario programado en que la cocina estuvo cerrada. Es distinto de % Carta activa: esta mide que la cocina abra, la otra que los platos estén disponibles una vez abierta."
          enlace="/foodcourt/tiempo-no-disponible"
        />
      </div>
    </template>
  </div>
</template>

<script>
import { Alert } from 'element-ui'
import CardMetricaCocina from '@/components/Metrics/CardMetricaCocina.vue'
import SelectorPeriodo from '@/components/Metrics/SelectorPeriodo.vue'
import MetricasCocinaGateway from '@/gateways/MetricasCocinaGateway'

/**
 * Métricas de la cocina OD del catering autenticado.
 *
 * Alcance (Sprint OPS): SOLO las 4 métricas visibles. El sistema A/B/C y el
 * pago por performance NO van acá — los define Humberto en su tarea. Por eso
 * ninguna card dice si el valor está bien o mal: eso exige umbrales acordados
 * que todavía no existen para estas métricas.
 *
 * Esta vista nunca envía una cocina: el backend la resuelve desde el token.
 */
export default {
  name: 'MetricasCocina',
  // Element UI no esta registrado globalmente en esta app: cada vista importa
  // lo que usa (misma convencion que ListFeedback.vue y el resto).
  components: {
    CardMetricaCocina,
    SelectorPeriodo,
    [Alert.name]: Alert
  },
  data () {
    return {
      // Los pone SelectorPeriodo al montarse, antes de la primera carga.
      desde: null,
      hasta: null,
      cargando: true,
      noDisponible: false,
      resumen: null,
      rango: null
    }
  },
  computed: {
    contextoCarta () {
      const sin = (this.resumen && this.resumen.carta_activa_dias_sin_dato) || 0
      return sin ? sin + ' día(s) sin medición' : 'Promedio del periodo'
    }
  },
  methods: {
    /* SelectorPeriodo emite tambien al montarse: esa es la primera carga. */
    alCambiarPeriodo (rango) {
      this.desde = rango.desde
      this.hasta = rango.hasta
      this.cargar()
    },
    fmtFecha (iso) {
      if (!iso) return ''
      const p = iso.split('-')
      return p[2] + '/' + p[1] + '/' + p[0]
    },
    cargar () {
      this.cargando = true
      this.noDisponible = false

      MetricasCocinaGateway.obtener(this.desde, this.hasta)
        .then(payload => {
          if (payload.status === 'UNAVAILABLE') {
            this.noDisponible = true
            return
          }
          const r = payload.result || {}
          this.resumen = r.resumen || null
          this.rango = r.rango || null
        })
        .catch(() => { this.noDisponible = true })
        .finally(() => { this.cargando = false })
    }
  }
}
</script>

<style scoped>
.metricas-cocina {
  padding: 28px 34px;
  /*
    Seguro defensivo: si por lo que sea (zoom del navegador, escalado de
    Windows, fuentes distintas) el contenido termina siendo mas ancho que el
    viewport, esto da una barra de scroll horizontal DENTRO de la vista en
    vez de recortar contenido sin forma de alcanzarlo. La causa raiz se sigue
    investigando (en las pruebas automatizadas el grid mide exacto, sin
    desborde), pero esto evita que quede algo inalcanzable mientras tanto.
  */
  overflow-x: auto;
}
.metricas-cocina__header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 26px;
}
.metricas-cocina__titulo {
  font-size: 34px;
  font-weight: 700;
  color: #1a202c;
  margin: 0;
}
.metricas-cocina__rango {
  font-size: 16px;
  font-weight: 400;
  color: #6b7684;
  margin-left: 14px;
}
.metricas-cocina__cards {
  display: grid;
  /* Con solo 4 cards y sin tabla debajo, estirarlas a lo ancho de toda la
     pantalla las dejaba con mucho espacio muerto adentro. Ancho fijo y
     compacto en vez de 1fr — se acomodan solas cuando no entran las 4 en
     una fila (auto-fill), sin depender de max-width en el contenedor. */
  grid-template-columns: repeat(auto-fill, minmax(230px, 260px));
  gap: 22px;
}
.metricas-cocina__cargando { padding: 60px 0; text-align: center; color: #8a94a0; font-size: 14px; }
</style>
